import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
    MessageSquare,
    Building, 
    Mail, 
    MapPin, 
    Loader2,
    Power,
    Globe
} from "lucide-react";

import type { TenantCreateRequest } from "@/api/tenants/tenants.types";

interface EnableMessagingDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onEnable: (data: TenantCreateRequest) => Promise<void>;
}

export const EnableMessagingDialog = ({ open, onOpenChange, onEnable }: EnableMessagingDialogProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState<TenantCreateRequest>({
        name: '',
        email: '',
        full_address: '',
        privacy_officer: '',
        partner_fee_cents: null,
        meta: null,
        client_general_settings: [],
        language: 'en' // Default to English
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            await onEnable(formData);
        } catch {
            // Error handling is done in parent component
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (field: keyof TenantCreateRequest, value: string | number | null) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[650px] w-[95vw] p-0 max-h-[90vh] flex flex-col">
                {/* Header */}
                <DialogHeader className="border-b bg-muted/20 px-6 py-3 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <MessageSquare className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <DialogTitle className="text-base font-semibold">
                                Enable Messaging Service
                            </DialogTitle>
                            <DialogDescription className="text-xs text-muted-foreground">
                                Set up WhatsApp messaging for your organization
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                {/* Content */}
                <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                    <div className="flex-1 overflow-y-auto px-6 py-4">
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="name" className="text-sm font-medium flex items-center gap-2">
                                    <Building className="h-4 w-4" />
                                    Organization Name *
                                </Label>
                                <Input
                                    id="name"
                                    placeholder="Enter organization name"
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    className="h-9"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-sm font-medium flex items-center gap-2">
                                        <Mail className="h-4 w-4" />
                                        Contact Email *
                                    </Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="contact@company.com"
                                        value={formData.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        className="h-9"
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="language" className="text-sm font-medium flex items-center gap-2">
                                        <Globe className="h-4 w-4" />
                                        Language *
                                    </Label>
                                    <Select
                                        value={formData.language || 'en'}
                                        onValueChange={(value: 'en' | 'de') => handleInputChange('language', value)}
                                    >
                                        <SelectTrigger className="h-9">
                                            <SelectValue placeholder="Select language" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="en">English (EN)</SelectItem>
                                            <SelectItem value="de">Deutsch (DE)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="address" className="text-sm font-medium flex items-center gap-2">
                                    <MapPin className="h-4 w-4" />
                                    Full Address *
                                </Label>
                                <Textarea
                                    id="address"
                                    placeholder="Enter complete business address"
                                    value={formData.full_address}
                                    onChange={(e) => handleInputChange('full_address', e.target.value)}
                                    rows={3}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="border-t bg-muted/20 px-6 py-3 flex items-center justify-end gap-3 shrink-0">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => onOpenChange(false)}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" size="sm" disabled={isLoading} className="gap-2 min-w-[140px]">
                            {isLoading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Enabling...
                                </>
                            ) : (
                                <>
                                    <Power className="h-4 w-4" />
                                    Enable Service
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}; 